import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { quizApi } from "../services/quizApi";
import QuizQuestion from "./QuizQuestion";
import QuizResults from "./QuizResults";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../styles/Quiz.css";

const Quiz = () => {
  const { quizId, letterId } = useParams();
  const navigate = useNavigate();
  const isDailyQuiz = !!letterId;

  // State management
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showAnswerForCurrentQuestion, setShowAnswerForCurrentQuestion] = useState(false);
  
  // Quiz preparation popup states
  const [showQuizPreparationPopup, setShowQuizPreparationPopup] = useState(false);
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);

  // Apply modern background class to body
  useEffect(() => {
    document.body.classList.add("quiz-body");
    return () => {
      document.body.classList.remove("quiz-body");
    };
  }, []);

  // Get user email and verification status from localStorage
  const userEmail = localStorage.getItem("userEmail");
  const userVerified = localStorage.getItem("userVerified");

  // Redirect to login page if not authenticated or verified
  useEffect(() => {
    if (!userEmail || !userVerified) {
      // Redirect to the main login page with a returnTo parameter
      const returnPath = isDailyQuiz ? `/quiz/daily/${letterId}` : `/quiz/${quizId}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnPath)}`);
    }
  }, [userEmail, userVerified, navigate, quizId, letterId, isDailyQuiz]);

  const fetchQuiz = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let quizData;
      if (isDailyQuiz) {
        // Fetch daily letter quiz using letterId
        quizData = await quizApi.getDailyQuiz(letterId, userEmail);
      } else {
        // Fetch regular quiz using quizId
        quizData = await quizApi.getQuiz(quizId, userEmail);
      }
      
      setQuiz(quizData);
      
      // Check if this is first attempt (no existing scores)
      const hasExistingAttempts = quizData.has_completed === true || 
                                 (quizData.total_attempts && quizData.total_attempts > 0) ||
                                 (quizData.first_score !== null && quizData.first_score !== undefined);
      setIsFirstAttempt(!hasExistingAttempts);
      
      // Show preparation popup if quiz is loaded
      setShowQuizPreparationPopup(true);
      
      // Set timer based on quiz type (default to 5 mins for daily, 10 mins for milestone)
      const timeLimit = quizData.time_limit_minutes || (isDailyQuiz ? 5 : 10);
      setTimeRemaining(timeLimit * 60); // Convert minutes to seconds
    } catch (err) {
      console.error('Quiz fetch error:', err);
      setError(err.message || "Failed to load quiz");
    } finally {
      setLoading(false);
    }
  }, [quizId, letterId, isDailyQuiz, userEmail]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  // Add beforeunload warning when quiz is started
  useEffect(() => {
    if (quizStarted && !results) {
      const handleBeforeUnload = (e) => {
        const answeredCount = Object.keys(answers).length;
        const totalQuestions = quiz?.questions?.length || 0;
        const currentScore = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
        
        e.preventDefault();
        e.returnValue = `You have answered ${answeredCount} out of ${totalQuestions} questions. Your current score would be ${currentScore}%. If you leave now, this will be submitted as your attempt. Are you sure you want to leave?`;
        return e.returnValue;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [quizStarted, results, answers, quiz]);

  const handleStartQuiz = () => {
    setShowQuizPreparationPopup(false);
    setQuizStarted(true);
    // Scroll to top when quiz starts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReviewLetter = () => {
    if (isDailyQuiz) {
      navigate(`/prepletters?letter=${letterId}`);
    } else {
      navigate('/quizzes');
    }
  };

  const handleSubmitQuiz = useCallback(async () => {
    // Ensure the answer for the last question is shown before submitting
    setShowAnswerForCurrentQuestion(true);
    // Allow user to see the answer/explanation before submitting
    // The actual submission will happen when the user clicks 'End Test' button (which calls this function)
    try {
      setSubmitting(true);
      setError(null);
      
      // Fill in 'Unattempted' for any unanswered questions
      const completeAnswers = { ...answers };
      if (quiz?.questions) {
        quiz.questions.forEach(question => {
          if (!completeAnswers[question.question_id]) {
            completeAnswers[question.question_id] = 'Unattempted';
          }
        });
      }
      
      let result;
      if (isDailyQuiz) {
        // For daily quizzes, we submit using the letterId
        const timeTaken = quiz?.time_limit_minutes * 60 - timeRemaining;
        result = await quizApi.submitDailyQuiz(letterId, completeAnswers, userEmail, timeTaken);
      } else {
        // For regular quizzes, we submit using the quizId
        result = await quizApi.submitQuiz(quizId, completeAnswers, userEmail);
      }
      
      setResults(result);
    } catch (err) {
      console.error('Quiz submission error:', err);
      setError(err.message || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
      setShowAnswerForCurrentQuestion(false); // Reset after submission
    }
  }, [quizId, letterId, isDailyQuiz, answers, userEmail, quiz, timeRemaining]);

  // Timer effect - only runs when quiz has actually started
  useEffect(() => {
    if (timeRemaining > 0 && !results && quizStarted) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !results && quizStarted) {
      handleSubmitQuiz(); // Auto-submit when time runs out
    }
  }, [timeRemaining, results, quizStarted, handleSubmitQuiz]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNextQuestion = () => {
    const currentQuestionId = quiz.questions[currentQuestionIndex].question_id;
    // Only proceed if an answer has been selected for the current question
    if (answers[currentQuestionId]) {
      setShowAnswerForCurrentQuestion(true); // Show the answer for the current question
      // No automatic timeout, user will click 'Move Next' or 'End Test'
    } else {
      // Optionally, provide user feedback that an answer is required
      toast.error('Please select an answer before proceeding.');
    }
  };



  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getProgressPercentage = () => {
    if (!quiz) return 0;
    return ((currentQuestionIndex + 1) / quiz.questions.length) * 100;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  if (loading) {
    return (
      <div className="quiz-state-container">
        <div className="spinner-modern"></div>
        <p className="quiz-state-text">Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container-modern">
        <div className="quiz-state-container">
          <div className="alert-modern alert-danger-modern">
            <h4>Error</h4>
            <p>{error}</p>
            <button
              className="btn-modern btn-secondary-modern mt-3"
              onClick={() => navigate("/quizzes")}
            >
              Back to Quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <QuizResults
        results={results}
        quiz={quiz}
        onReturnToQuizzes={() => navigate("/quizzes")}
      />
    );
  }

  if (!quiz) {
    return (
      <div className="quiz-container-modern">
        <div className="alert-modern alert-danger-modern">Quiz not found</div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];

  return (
    <div className="quiz-container-modern">
      {/* Quiz Preparation Popup */}
      {showQuizPreparationPopup && (
        <div className="quiz-overlay">
          <div className="quiz-modal quiz-preparation-modal">
            <div className="quiz-modal-header text-center">
              <h4 className="quiz-title-modern">
                <i className="bi bi-clipboard-check me-2"></i>
                {isDailyQuiz ? `Ready for Letter #${letterId} Quiz?` : `Ready for Quiz?`}
              </h4>
              <p className="quiz-description-modern mb-0">
                {quiz.title}
              </p>
            </div>
            
            <div className="quiz-modal-body">
              <div className="quiz-instructions">
                {isFirstAttempt && (
                  <div className="alert-modern alert-warning-modern mb-4">
                    <h6 className="text-light mb-3">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      First Attempt is Important!
                    </h6>
                    <p className="mb-2">Your first attempt score will be your <strong>official score</strong> and may affect your ranking.</p>
                    <p className="mb-0">{isDailyQuiz ? `Make sure you've thoroughly read PrepLetter #${letterId} and tried the practice problems.` : 'Make sure you\'re well prepared for this milestone quiz.'}</p>
                  </div>
                )}

                {!isFirstAttempt && (
                  <div className="alert-modern alert-info-modern mb-4">
                    <h6 className="text-light mb-3">
                      <i className="bi bi-info-circle-fill me-2"></i>
                      Retake Attempt
                    </h6>
                    <p className="mb-0">This is a retake attempt. Your first attempt score was your official score, but you can still practice and improve your understanding.</p>
                  </div>
                )}

                <div className="quiz-rules mb-4">
                  <h6 className="text-light mb-3">
                    <i className="bi bi-shield-check me-2"></i>
                    Quiz Guidelines
                  </h6>
                  <ul className="quiz-rules-list">
                    <li>Complete the quiz independently without external help</li>
                    <li>Do not review letter content during the quiz</li>
                    <li>Once started, quiz will be scored even if you exit early</li>
                    <li>Take your time and read each question carefully</li>
                  </ul>
                </div>

                <div className="quiz-recommendation">
                  <p className="quiz-description-modern text-center mb-0">
                    <strong>{isDailyQuiz ? 'Recommendation:' : 'Ready to proceed?'}</strong> {isDailyQuiz ? 'If you haven\'t finished reading the letter, review it first for better results.' : 'The quiz will start immediately when you click "Start Quiz".'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="quiz-modal-footer">
              <div className="d-flex justify-content-center gap-3 w-100">
                {isDailyQuiz && (
                  <button 
                    className="btn-modern btn-info-modern"
                    onClick={handleReviewLetter}
                  >
                    <i className="bi bi-book me-2"></i>
                    Review Letter First
                  </button>
                )}
                <button 
                  className="btn-modern btn-primary-modern"
                  onClick={handleStartQuiz}
                >
                  <i className="bi bi-play-circle me-2"></i>
                  I'm Ready - Start Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {quizStarted && (
        <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      {/* Quiz Header */}
      <div className="quiz-card">
        <div className="quiz-card-header d-flex justify-content-between align-items-center">
          <div>
            <h1 className="quiz-title-modern">{quiz.title}</h1>
            <p className="quiz-description-modern">{quiz.description}</p>
          </div>
          {timeRemaining !== null && (
            <div className={`timer-modern ${timeRemaining <= 60 ? 'warning' : ''}`}>
              {formatTime(timeRemaining)}
            </div>
          )}
        </div>
        <div className="quiz-card-body">
          {/* Progress Bar */}
          <div className="progress-container mb-3">
            <div
              className="progress-bar-modern"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
          <div className="text-center text-light mb-4">
            <small>
              Question {currentQuestionIndex + 1} of {quiz.questions.length}
            </small>
          </div>

          {/* Question Component */}
          <QuizQuestion
            question={currentQuestion}
            selectedAnswer={answers[currentQuestion.question_id]}
            onAnswerChange={showAnswerForCurrentQuestion ? () => {} : (answer) =>
              handleAnswerChange(currentQuestion.question_id, answer)
            }
            showAnswer={showAnswerForCurrentQuestion}
            key={currentQuestion.question_id} // Add key for re-rendering on question change
          />
        </div>
        <div className="quiz-card-footer d-flex justify-content-end">

          {currentQuestionIndex < quiz.questions.length - 1 ? (
            showAnswerForCurrentQuestion ? (
              <button
                className="btn-modern btn-primary-modern"
                onClick={() => {
                  setCurrentQuestionIndex(currentQuestionIndex + 1);
                  setShowAnswerForCurrentQuestion(false);
                  // Scroll to top when moving to next question
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Move Next
              </button>
            ) : (
              <button
                className="btn-modern btn-primary-modern"
                onClick={handleNextQuestion}
                disabled={!answers[currentQuestion.question_id]}
              >
                Next
              </button>
            )
          ) : (
            showAnswerForCurrentQuestion ? (
              <div className="end-test-container">
                <button
                  className="btn-modern btn-submit-modern"
                  onClick={handleSubmitQuiz}
                  disabled={submitting || getAnsweredCount() === 0}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Submitting...
                    </>
                  ) : (
                    'End Test'
                  )}
                </button>
              </div>
            ) : (
              <button
                className="btn-modern btn-primary-modern"
                onClick={handleNextQuestion}
                disabled={!answers[currentQuestion.question_id]}
              >
                Review Answer
              </button>
            )
          )}
        </div>
      </div>

      {/* Question Overview */}
      <div className="quiz-card mt-4">
        <div className="quiz-card-header">
          <h5 className="mb-0 text-light">Question Overview</h5>
        </div>
        <div className="quiz-card-body">
          <div className="d-flex flex-wrap gap-2">
            {quiz.questions.map((q, index) => (
              <button
                key={q.question_id}
                className={`btn-modern btn-sm ${index === currentQuestionIndex
                    ? 'btn-primary-modern'
                    : answers[q.question_id]
                      ? 'btn-secondary-modern active'
                      : 'btn-secondary-modern'
                  }`}
                style={{ width: '50px', height: '50px', padding: 0, flexShrink: 0 }}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bug Report Link */}
      <div className="quiz-bug-report mt-3">
        <a 
          href={`/bug-report?page=quiz&entity=${isDailyQuiz ? `daily-quiz-${letterId}` : `quiz-${quizId}`}`} 
          className="bug-report-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          🐛 Report a Bug
        </a>
      </div>
        </>
      )}
    </div>
  );
};

export default Quiz;
